import { Badge, Card, Checkbox, Flex, Loader, Stack, Text } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import type { IntakeAnswers, IntakeAction } from '../types';
import { useIntakeDataContext } from '../IntakeDataContext';

const NO_PREFERENCE = 'no-preference';

interface StepLocationProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
}

export function StepLocation({ answers, dispatch }: StepLocationProps): JSX.Element {
  const { locations, locationsLoading } = useIntakeDataContext();
  const selected = answers.preferredLocationIds;
  const isNoPreference = selected.includes(NO_PREFERENCE);

  function handleToggle(id: string) {
    if (id === NO_PREFERENCE) {
      dispatch({ type: 'SET_PREFERRED_LOCATION_IDS', payload: isNoPreference ? [] : [NO_PREFERENCE] });
      return;
    }
    const withoutNoPref = selected.filter((s) => s !== NO_PREFERENCE);
    const next = withoutNoPref.includes(id)
      ? withoutNoPref.filter((s) => s !== id)
      : [...withoutNoPref, id];
    dispatch({ type: 'SET_PREFERRED_LOCATION_IDS', payload: next });
  }

  if (locationsLoading) {
    return (
      <Stack gap="md" align="center" py="xl">
        <Loader size="md" color="gold" />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Select one or more office locations you&apos;d be willing to visit for in-person sessions.
      </Text>

      {locations.map((loc) => {
        const checked = selected.includes(loc.id);
        return (
          <Card
            key={loc.id}
            withBorder
            radius="md"
            padding="sm"
            onClick={() => handleToggle(loc.id)}
            style={{
              cursor: 'pointer',
              borderColor: checked ? 'var(--mantine-color-gold-5)' : undefined,
              background: checked ? 'var(--mantine-color-gold-0)' : undefined,
            }}
          >
            <Flex align="center" gap="sm">
              <Checkbox
                checked={checked}
                onChange={() => handleToggle(loc.id)}
                color="gold"
                tabIndex={-1}
                styles={{ input: { cursor: 'pointer' } }}
              />
              <Flex align="center" gap="xs" style={{ flex: 1 }}>
                <IconMapPin size={14} style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }} />
                <Text fw={500} size="sm">{loc.address}</Text>
                {loc.distanceMiles != null && (
                  <Badge size="xs" variant="light" color="gray">
                    {loc.distanceMiles} mi
                  </Badge>
                )}
              </Flex>
            </Flex>
          </Card>
        );
      })}

      <Card
        withBorder
        radius="md"
        padding="sm"
        onClick={() => handleToggle(NO_PREFERENCE)}
        style={{
          cursor: 'pointer',
          borderColor: isNoPreference ? 'var(--mantine-color-gold-5)' : undefined,
          background: isNoPreference ? 'var(--mantine-color-gold-0)' : undefined,
        }}
      >
        <Flex align="center" gap="sm">
          <Checkbox
            checked={isNoPreference}
            onChange={() => handleToggle(NO_PREFERENCE)}
            color="gold"
            tabIndex={-1}
            styles={{ input: { cursor: 'pointer' } }}
          />
          <Text fw={500} size="sm">No preference</Text>
        </Flex>
      </Card>
    </Stack>
  );
}
